import type { ReactNode } from 'react';

// Render plain text with bare URLs turned into safe external links. Used by
// the Plan overview block (Slice A polish Fix 1) — no existing utility
// existed. Pure: given a string, returns an array of text strings and <a>
// elements. The caller is responsible for whitespace handling (the overview
// block wraps this in `whitespace-pre-wrap`, so newlines survive as text).
//
// Matching rules:
//   - http:// and https:// absolute URLs
//   - bare www. hosts (rendered as-is, href prefixed with https://)
//   - trailing sentence punctuation ( . , ; : ! ? ) ] " ' ) is treated as
//     prose, not part of the URL, so "see https://x.com." links just the URL
//
// Not an HTML sanitiser — input is plain text rendered as React children, so
// there is no markup-injection surface. rel="noopener noreferrer" + target
// _blank per the brief.

const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCT = /[.,;:!?'")\]]+$/;

export function linkifyText(text: string): ReactNode {
  if (!text) return text;

  const nodes: ReactNode[] = [];
  let cursor = 0; // index into `text` of the next un-emitted character
  let key = 0;
  URL_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = URL_RE.exec(text)) !== null) {
    const start = match.index;
    const raw = match[0];

    // Strip trailing prose punctuation from the link target; those chars are
    // re-emitted as normal text via the next text slice (cursor only advances
    // past the trimmed URL, not the punctuation the regex also consumed).
    const trail = raw.match(TRAILING_PUNCT)?.[0] ?? '';
    const url = trail ? raw.slice(0, raw.length - trail.length) : raw;

    // Defensive: a match that is entirely punctuation (e.g. "www.") collapses
    // to empty. Skip linking; the chars fall through as text.
    if (url.length === 0) continue;

    if (start > cursor) nodes.push(text.slice(cursor, start));

    const href = url.toLowerCase().startsWith('www.')
      ? `https://${url}`
      : url;
    nodes.push(
      <a
        key={`lt-${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline break-words"
      >
        {url}
      </a>,
    );

    cursor = start + url.length;
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}
